import type { Meeting, MeetingRecord } from 'type/meeting';

type ConflictRecord = Pick<MeetingRecord, 'inviteState' | 'conflict'> & {
  meeting: Pick<Meeting, 'meetingNo' | 'beginTime' | 'endTime' | 'status'> & Partial<Pick<Meeting, 'state'>>;
};

type ConflictRange = { beginTime: number; endTime: number };

const isAbsent = ({ inviteState }: ConflictRecord) => inviteState === 1 || inviteState === 3;

const canConflict = ({ inviteState, meeting }: ConflictRecord, now: number) => {
  const active = meeting.status
    ? ['BEFORE', 'DURING'].includes(meeting.status)
    : meeting.state === 0 || meeting.state === 1;
  return active && (inviteState === 0 || inviteState === 2) &&
    meeting.beginTime < meeting.endTime && meeting.endTime > now;
};

const overlaps = (left: ConflictRecord, right: ConflictRecord) => {
  const a = left.meeting;
  const b = right.meeting;
  const sameMeeting = left === right || (a.meetingNo && a.meetingNo === b.meetingNo && a.beginTime === b.beginTime);
  return !sameMeeting && a.beginTime < b.endTime && b.beginTime < a.endTime;
};

const hasActiveConflict = (record: ConflictRecord, context: ConflictRecord[], now: number) =>
  context.some((other) => canConflict(other, now) && overlaps(record, other));

/** 与 h5-client 一致：请假、拒绝不再构成冲突，其他接口标记保持不变。 */
export const getDisplayConflictRecords = <T extends ConflictRecord>(records: T[], context: ConflictRecord[] = records, now = Date.now()): T[] =>
  records.map((record) => {
    const onlyAbsentConflicts = record.conflict && canConflict(record, now) &&
      context.some((other) => isAbsent(other) && overlaps(record, other)) &&
      !hasActiveConflict(record, context, now);
    return isAbsent(record) || onlyAbsentConflicts ? { ...record, conflict: 0 } : record;
  });

/** 分页数据不完整，仅在无法确认有效冲突时补查对应时间段。 */
export const resolvePagedDisplayConflicts = async <T extends ConflictRecord>(
  records: T[],
  loadContext: (range: ConflictRange) => Promise<ConflictRecord[] | undefined>,
  now = Date.now()
): Promise<T[]> => {
  const unresolved = records.filter((record) => record.conflict && canConflict(record, now) &&
    !hasActiveConflict(record, records, now));
  if (!unresolved.length) return getDisplayConflictRecords(records, records, now);

  const context = await loadContext({
    beginTime: Math.min(...unresolved.map((record) => record.meeting.beginTime)),
    endTime: Math.max(...unresolved.map((record) => record.meeting.endTime))
  });
  // 补查失败时保留接口标记，避免使用不完整列表清除真实冲突。
  return getDisplayConflictRecords(records, context || [], now);
};
